import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectIncidentGuid, selectUserGuid } from 'slices/commonSelectors';
import {
  CreateIncidentStatusSummaryRequestBody,
  IncidentStatusSummaryDto,
  UpdateIncidentStatusSummaryRequestBody,
} from 'types/iapBaseTypes';

export const keys = {
  incidentStatusSummary: 'incidentStatusSummary',
} as const;

export const useIncidentStatusSummaries = (incidentId: string) => {
  return useQuery({
    queryKey: [keys.incidentStatusSummary, incidentId],
    queryFn: async () => {
      const response = await axios.get<IncidentStatusSummaryDto[]>(
        `${config.apiGateway.ics_incident_status_summary}/${incidentId}`
      );
      return response.data;
    },
    enabled: !!incidentId,
  });
};

export const useCreateIncidentStatusSummary = () => {
  const queryClient = useQueryClient();
  const incidentId = useSelector(selectIncidentGuid);
  const userGuid = useSelector(selectUserGuid);

  return useMutation({
    mutationFn: async (
      data: Omit<
        CreateIncidentStatusSummaryRequestBody,
        'incidentId' | 'userGuid'
      >
    ) => {
      const payload: CreateIncidentStatusSummaryRequestBody = {
        ...data,
        incidentId: incidentId!,
        userGuid: userGuid!,
      };

      const response = await axios.post<IncidentStatusSummaryDto>(
        config.apiGateway.ics_incident_status_summary,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      if (incidentId) {
        queryClient.invalidateQueries({
          queryKey: [keys.incidentStatusSummary, incidentId],
        });
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error || 'Failed to create safety plan';
      toast.error(errorMessage);
    },
  });
};

export const useUpdateIncidentStatusSummary = () => {
  const queryClient = useQueryClient();
  const incidentId = useSelector(selectIncidentGuid);

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Omit<
        UpdateIncidentStatusSummaryRequestBody,
        'incidentId' | 'userGuid'
      >;
    }) => {
      const payload: UpdateIncidentStatusSummaryRequestBody = {
        ...data,
        incidentId: incidentId,
      };

      const response = await axios.put<IncidentStatusSummaryDto>(
        `${config.apiGateway.ics_incident_status_summary}/${id}`,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      if (incidentId) {
        queryClient.invalidateQueries({
          queryKey: [keys.incidentStatusSummary, incidentId],
        });
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error ||
        'Failed to update incident status summary';
      toast.error(errorMessage);
    },
  });
};

export const useUpsertIncidentStatusSummary = (id?: string) => {
  const { mutate: createIncidentStatusSummary } =
    useCreateIncidentStatusSummary();

  const { mutate: updateIncidentStatusSummary } =
    useUpdateIncidentStatusSummary();

  if (id) {
    return (
      data: Omit<
        UpdateIncidentStatusSummaryRequestBody,
        'incidentId' | 'userGuid'
      >
    ) => updateIncidentStatusSummary({ id, data });
  } else {
    return createIncidentStatusSummary;
  }
};

export const useDeleteIncidentStatusSummary = () => {
  const queryClient = useQueryClient();
  const incidentId = useSelector(selectIncidentGuid);

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `${config.apiGateway.ics_incident_status_summary}/${id}`
      );
      return response.data;
    },
    onSuccess: () => {
      if (incidentId) {
        queryClient.invalidateQueries({
          queryKey: [keys.incidentStatusSummary, incidentId],
        });
      }
      toast.success('Incident status summary deleted successfully');
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error ||
        'Failed to delete incident status summary';
      toast.error(errorMessage);
    },
  });
};
